import React, { useEffect, useRef, useState } from "react";
import { Camera, X, RotateCw, AlertCircle, CheckCircle2 } from "lucide-react";
import { Html5Qrcode } from "html5-qrcode";
import { Button } from "../ui/button";

/**
 * Modal de scan caméra natif basé sur html5-qrcode.
 *
 * - Demande l'accès caméra navigateur (préférence "environment" sur mobile/tablette)
 * - Détecte EAN-13, EAN-8, Code128, QR Code, Code39, ITF, UPC
 * - Vibre + jingle sonore au scan réussi
 * - Bouton pour basculer entre caméras (smartphone double-cam)
 * - Fermé automatiquement après un scan (mode "one-shot")
 *
 * Usage :
 *   <CameraScanner
 *     open={open}
 *     onClose={() => setOpen(false)}
 *     onDetected={(code) => {
 *       // Insérer le code dans ton input scan + déclencher la soumission
 *     }}
 *   />
 *
 * data-testid pour l'agent de test :
 *   - camera-scanner-modal
 *   - camera-scanner-close
 *   - camera-scanner-switch
 *   - camera-scanner-status
 */
export default function CameraScanner({ open, onClose, onDetected }) {
  const containerRef = useRef(null);
  const qrCodeRef = useRef(null);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState("init"); // init | starting | running | scanned | error
  const [cameras, setCameras] = useState([]);
  const [activeCamId, setActiveCamId] = useState(null);
  const [lastCode, setLastCode] = useState(null);

  // Démarrage automatique à l'ouverture
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setError(null);
    setLastCode(null);
    setStatus("starting");

    (async () => {
      try {
        // Phase 87bd — Vérif d'environnement : `mediaDevices` doit exister (HTTPS + browser moderne)
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
          throw new Error("Le navigateur ne supporte pas l'accès caméra (mediaDevices indisponible). Utilisez Firefox, Brave ou Chromium à jour, en HTTPS.");
        }

        // Phase 87bd — Force la prompt permission AVANT getCameras().
        // Sur Firefox/Brave Android, Html5Qrcode.getCameras() ne demande PAS la permission ;
        // il liste juste les caméras visibles sans accès. Sans prompt explicite, getCameras
        // retourne souvent 0 device sur ces navigateurs.
        try {
          const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } });
          // Libère immédiatement le stream — on relance via html5-qrcode ensuite
          stream.getTracks().forEach((t) => t.stop());
        } catch (permErr) {
          // Permission refusée → message dédié
          throw permErr;
        }

        if (cancelled) return;

        // 1) Lister les caméras dispo
        const devices = await Html5Qrcode.getCameras();
        if (cancelled) return;
        if (!devices || devices.length === 0) {
          setError("Aucune caméra détectée. Vérifiez que la caméra n'est pas désactivée dans les paramètres système.");
          setStatus("error");
          return;
        }
        setCameras(devices);

        // Prefer la caméra arrière (environment) sur mobile/tablette
        const back = devices.find((d) => /back|arrière|rear|environment/i.test(d.label));
        const camId = (back || devices[0]).id;
        setActiveCamId(camId);

        await startCamera(camId);
      } catch (e) {
        if (!cancelled) {
          setError(humanizeCameraError(e));
          setStatus("error");
        }
      }
    })();

    return () => {
      cancelled = true;
      stopCamera();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const startCamera = async (camId) => {
    if (!containerRef.current) return;
    const qr = new Html5Qrcode(containerRef.current.id, { verbose: false });
    qrCodeRef.current = qr;
    try {
      await qr.start(
        camId,
        {
          fps: 10,
          // qrbox dynamique adapté au container
          qrbox: (vw, vh) => {
            const min = Math.min(vw, vh);
            return { width: Math.round(min * 0.75), height: Math.round(min * 0.45) };
          },
          aspectRatio: 1.7777,
          // Formats acceptés : EAN-13 + tous les classiques
          // (html5-qrcode supporte ces formats par défaut)
        },
        (decodedText) => {
          // Succès — protection contre les multi-déclenchements rapides
          setLastCode(decodedText);
          setStatus("scanned");
          // Vibration tactile (mobile)
          if (navigator.vibrate) navigator.vibrate(80);
          // Beep court
          try {
            const ctx = new (window.AudioContext || window.webkitAudioContext)();
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.connect(gain);
            gain.connect(ctx.destination);
            osc.frequency.value = 880;
            gain.gain.value = 0.1;
            osc.start();
            setTimeout(() => { osc.stop(); ctx.close(); }, 120);
          } catch {/* audio context may fail in iframe */}
          // Stop puis callback
          qr.stop().then(() => {
            onDetected(decodedText);
            // L'appelant fermera (ou laissera ouvert pour multi-scan)
          }).catch(() => {});
        },
        () => { /* scan échoué = silence (chaque frame qui ne décode pas) */ },
      );
      setStatus("running");
    } catch (e) {
      setError(humanizeCameraError(e));
      setStatus("error");
    }
  };

  const stopCamera = async () => {
    try {
      if (qrCodeRef.current) {
        const state = qrCodeRef.current.getState && qrCodeRef.current.getState();
        // 2 = SCANNING
        if (state === 2) {
          await qrCodeRef.current.stop();
        }
        await qrCodeRef.current.clear();
      }
    } catch {/* ignore */}
    qrCodeRef.current = null;
  };

  const switchCamera = async () => {
    if (cameras.length < 2) return;
    await stopCamera();
    const idx = cameras.findIndex((c) => c.id === activeCamId);
    const next = cameras[(idx + 1) % cameras.length];
    setActiveCamId(next.id);
    setStatus("starting");
    await startCamera(next.id);
  };

  const handleClose = async () => {
    await stopCamera();
    onClose();
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/95 backdrop-blur-sm flex flex-col"
      data-testid="camera-scanner-modal"
    >
      {/* Header */}
      <header className="flex items-center justify-between px-4 py-3 border-b border-white/10">
        <div className="flex items-center gap-2 text-white">
          <Camera className="w-5 h-5 text-[#C1A063]" />
          <h3 className="font-heading text-lg">Scan caméra</h3>
        </div>
        <div className="flex items-center gap-2">
          {cameras.length > 1 && (
            <Button
              variant="ghost"
              size="icon"
              onClick={switchCamera}
              className="text-white hover:bg-white/10"
              title="Changer de caméra"
              data-testid="camera-scanner-switch"
            >
              <RotateCw className="w-4 h-4" />
            </Button>
          )}
          <Button
            variant="ghost"
            size="icon"
            onClick={handleClose}
            className="text-white hover:bg-white/10"
            data-testid="camera-scanner-close"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>
      </header>

      {/* Camera view */}
      <div className="flex-1 flex items-center justify-center px-4 py-6 relative">
        <div
          id="camera-scanner-viewport"
          ref={containerRef}
          className="w-full max-w-2xl aspect-video bg-black rounded-lg overflow-hidden border border-white/10 shadow-2xl"
        />
        {/* Overlay status */}
        {status !== "running" && (
          <div className="absolute inset-0 flex items-center justify-center px-4">
            <div
              className={`bg-black/85 px-5 py-4 rounded-lg text-white text-sm max-w-lg flex flex-col items-center gap-3 ${status === "error" ? "border border-[#E54D2E]/40" : ""}`}
              data-testid="camera-scanner-status"
            >
              {status === "starting" && (
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                  Démarrage caméra…
                </div>
              )}
              {status === "scanned" && (
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-[#88A066]" />
                  Code reçu : <span className="font-mono text-[#C1A063]">{lastCode}</span>
                </div>
              )}
              {status === "error" && (
                <>
                  <div className="flex items-start gap-2">
                    <AlertCircle className="w-5 h-5 text-[#E54D2E] mt-0.5 shrink-0" />
                    <span className="leading-relaxed text-[13px]">{error}</span>
                  </div>
                  <Button
                    onClick={async () => {
                      setError(null);
                      setStatus("starting");
                      // Force la re-prompt permission + re-listing caméras
                      try {
                        if (navigator.mediaDevices?.getUserMedia) {
                          const s = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } });
                          s.getTracks().forEach((t) => t.stop());
                        }
                        const devices = await Html5Qrcode.getCameras();
                        if (!devices?.length) throw new Error("Aucune caméra détectée");
                        setCameras(devices);
                        const back = devices.find((d) => /back|arrière|rear|environment/i.test(d.label));
                        const camId = (back || devices[0]).id;
                        setActiveCamId(camId);
                        await startCamera(camId);
                      } catch (e) {
                        setError(humanizeCameraError(e));
                        setStatus("error");
                      }
                    }}
                    className="bg-[#C1A063] hover:bg-[#A88747] text-black"
                    size="sm"
                    data-testid="camera-scanner-retry"
                  >
                    <RotateCw className="w-4 h-4 mr-1.5" /> Réessayer
                  </Button>
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Footer hints */}
      <footer className="px-4 py-3 border-t border-white/10 text-center">
        <p className="text-xs text-white/60">
          Cadrez le code-barres EAN-13 dans la zone centrale.
          {cameras.length > 1 && " Astuce : utilisez le bouton ↻ pour basculer vers la caméra arrière."}
        </p>
      </footer>
    </div>
  );
}

function humanizeCameraError(e) {
  const msg = e?.message || String(e || "");
  const name = e?.name || "";

  // Permission refusée explicitement (la cause #1 sur Firefox/Brave Android)
  if (name === "NotAllowedError" || /permission|denied|notallowed/i.test(msg)) {
    return [
      "Permission caméra refusée par le navigateur.",
      "Sur Firefox Android : touchez l'icône cadenas dans la barre d'adresse → Permissions → Caméra → Autoriser.",
      "Sur Brave Android : icône lion (Shields) → désactiver pour ce site, puis touchez cadenas → Caméra → Autoriser.",
      "Sinon : paramètres du navigateur → Sites et téléchargements → Caméra → retrouvez maison-thai.ch et autorisez.",
    ].join(" ");
  }
  if (name === "NotFoundError" || /notfound|no.+camera/i.test(msg)) {
    return "Aucune caméra disponible sur cet appareil. Vérifiez qu'aucune autre app ne l'utilise et qu'elle est bien connectée.";
  }
  if (name === "NotReadableError" || /inuse|in use|notreadable/i.test(msg)) {
    return "La caméra est utilisée par une autre application (visio, scan, etc.). Fermez l'app concurrente et réessayez.";
  }
  if (name === "SecurityError" || /secure|https/i.test(msg)) {
    return "Le navigateur exige une connexion HTTPS pour la caméra. Si vous êtes sur HTTP, basculez sur https://maison-thai.ch (avec le S).";
  }
  if (name === "OverconstrainedError" || /constraint/i.test(msg)) {
    return "La caméra disponible ne supporte pas la résolution demandée. Essayez avec le bouton ↻ pour basculer sur l'autre caméra.";
  }
  return msg.slice(0, 200) || "Erreur d'accès caméra inconnue.";
}
